import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneTilt, ChatCircleDots } from "@phosphor-icons/react";

const roleLabels = {
    admin: "Amministratore",
    coach: "Allenatore",
    player: "Giocatore",
    parent: "Genitore",
};

export default function Messages() {
    const { user } = useAuth();
    const { refresh: refreshNotifs } = useNotifications();
    const [contacts, setContacts] = useState([]);
    const [selected, setSelected] = useState(null);
    const [thread, setThread] = useState([]);
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const endRef = useRef(null);
    const pollRef = useRef(null);

    const loadContacts = useCallback(async () => {
        try {
            const { data } = await api.get("/messages/contacts");
            setContacts(data);
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("contacts load failed:", e);
            }
        }
    }, []);

    useEffect(() => {
        loadContacts();
    }, [loadContacts]);

    const loadThread = async (contact) => {
        setSelected(contact);
        try {
            const { data } = await api.get(`/messages/thread/${contact.id}`);
            setThread(data);
            await api.put(`/messages/thread/${contact.id}/read`);
            refreshNotifs();
            loadContacts();
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("thread load failed:", e);
            }
        }
    };

    // Poll thread every 15s while a chat is open
    useEffect(() => {
        if (!selected) return;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/messages/thread/${selected.id}`);
                setThread(data);
                await api.put(`/messages/thread/${selected.id}/read`);
                refreshNotifs();
            } catch (e) {
                if (process.env.NODE_ENV !== "production") {
                    console.warn("thread poll failed:", e);
                }
            }
        }, 15000);
        return () => clearInterval(pollRef.current);
    }, [selected, refreshNotifs]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [thread]);

    const send = async (e) => {
        e.preventDefault();
        if (!selected || !body.trim()) return;
        setSending(true);
        try {
            const { data } = await api.post("/messages", {
                to_user_id: selected.id,
                body: body.trim(),
            });
            setThread((t) => [...t, data]);
            setBody("");
            loadContacts();
            refreshNotifs();
        } catch {
            toast.error("Errore invio messaggio");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="messages-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Conversazioni
                    </div>
                    <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight">
                        Messaggi
                    </h1>
                </div>
                {selected && (
                    <button
                        data-testid="back-to-contacts"
                        onClick={() => {
                            setSelected(null);
                            setThread([]);
                        }}
                        className="md:hidden inline-flex items-center gap-1 text-xs uppercase tracking-widest text-primary press"
                    >
                        <ArrowLeft size={14} weight="bold" /> Contatti
                    </button>
                )}
            </div>

            <div className="grid md:grid-cols-[300px_1fr] gap-4 h-[70vh]">
                {/* Contacts list */}
                <div
                    className={`rounded-2xl hairline bg-card overflow-y-auto ${
                        selected ? "hidden md:block" : ""
                    }`}
                >
                    {contacts.length === 0 && (
                        <div className="p-6 text-center text-sm text-muted-foreground">
                            <ChatCircleDots size={28} weight="duotone" className="mx-auto mb-2" />
                            Nessun contatto ancora disponibile.
                        </div>
                    )}
                    {contacts.map((c) => (
                        <button
                            key={c.id}
                            data-testid={`contact-${c.id}`}
                            onClick={() => loadThread(c)}
                            className={`w-full text-left p-3 border-b border-border/60 press hover:bg-muted/60 flex items-center gap-3 ${
                                selected?.id === c.id ? "bg-muted/60" : ""
                            }`}
                        >
                            <div className="w-10 h-10 rounded-full bg-primary/15 text-primary grid place-items-center font-display font-bold shrink-0">
                                {c.name
                                    ?.split(" ")
                                    .map((s) => s[0])
                                    .join("")
                                    .slice(0, 2)
                                    .toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <div className="font-semibold truncate">{c.name}</div>
                                    {c.unread > 0 && (
                                        <span
                                            data-testid="contact-unread-badge"
                                            className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center"
                                        >
                                            {c.unread}
                                        </span>
                                    )}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    {roleLabels[c.role]}
                                </div>
                                {c.last_message && (
                                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                                        {c.last_message.body}
                                    </div>
                                )}
                            </div>
                        </button>
                    ))}
                </div>

                {/* Thread */}
                <div
                    className={`rounded-2xl hairline bg-card flex flex-col overflow-hidden ${
                        !selected ? "hidden md:flex" : "flex"
                    }`}
                >
                    {!selected ? (
                        <div className="flex-1 grid place-items-center text-sm text-muted-foreground p-6 text-center">
                            <div>
                                <ChatCircleDots size={36} weight="duotone" className="mx-auto mb-2 opacity-60" />
                                Seleziona un contatto per iniziare la conversazione.
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="p-4 border-b border-border/60 flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-primary/15 text-primary grid place-items-center font-display font-bold">
                                    {selected.name
                                        ?.split(" ")
                                        .map((s) => s[0])
                                        .join("")
                                        .slice(0, 2)
                                        .toUpperCase()}
                                </div>
                                <div>
                                    <div className="font-semibold">{selected.name}</div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                        {roleLabels[selected.role]}
                                    </div>
                                </div>
                            </div>
                            <div
                                data-testid="messages-thread"
                                className="flex-1 overflow-y-auto p-4 space-y-2 bg-field"
                            >
                                {thread.length === 0 && (
                                    <div className="text-center text-sm text-muted-foreground py-8">
                                        Nessun messaggio ancora. Scrivi il primo!
                                    </div>
                                )}
                                {thread.map((m) => {
                                    const mine = m.from_user_id === user.id;
                                    return (
                                        <div
                                            key={m.id}
                                            className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                        >
                                            <div
                                                className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                                                    mine
                                                        ? "bg-primary text-primary-foreground rounded-br-sm"
                                                        : "bg-muted text-foreground rounded-bl-sm"
                                                }`}
                                            >
                                                <div className="whitespace-pre-wrap break-words">
                                                    {m.body}
                                                </div>
                                                <div
                                                    className={`text-[10px] mt-1 ${
                                                        mine
                                                            ? "text-primary-foreground/70"
                                                            : "text-muted-foreground"
                                                    }`}
                                                >
                                                    {new Date(m.created_at).toLocaleTimeString(
                                                        "it-IT",
                                                        { hour: "2-digit", minute: "2-digit" }
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={endRef} />
                            </div>
                            <form onSubmit={send} className="p-3 border-t border-border/60 flex items-end gap-2">
                                <Textarea
                                    data-testid="message-body-input"
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            send(e);
                                        }
                                    }}
                                    placeholder="Scrivi un messaggio..."
                                    rows={1}
                                    className="min-h-11 max-h-32 resize-none"
                                />
                                <Button
                                    data-testid="send-message-btn"
                                    type="submit"
                                    disabled={sending || !body.trim()}
                                    className="h-11 press"
                                >
                                    <PaperPlaneTilt size={16} weight="fill" />
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
